import { useI18n } from '@ecom/i18n';
import { Button, Field, Input } from '@ecom/ui-kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReactElement } from 'react';
import { useForm } from 'react-hook-form';

import { createAdministratorFormSchema, type CreateAdministratorFormValues } from '../schemas';

import { whenDefined } from '@/lib/optional-props';

/**
 * `POST /admin/platform-accounts` - a superadmin creates a platform ADMINISTRATOR. There is no role field on purpose: the new account
 * is an Administrator, never a Superadmin (that takes two superadmins, see the promotions tab).
 *
 * `onSubmit` must REJECT on failure so the values survive a refusal. The password field is the one exception: it is cleared in every
 * case so that it does not linger in the DOM.
 */
export function CreateAdministratorForm({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (values: CreateAdministratorFormValues) => Promise<void>;
  isSubmitting: boolean;
}): ReactElement {
  const { t } = useI18n();
  const {
    register,
    handleSubmit,
    reset,
    resetField,
    formState: { errors },
  } = useForm<CreateAdministratorFormValues>({
    resolver: zodResolver(createAdministratorFormSchema),
    defaultValues: { email: '', displayName: '', reason: '', confirmPassword: '' },
  });

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(async (values) => {
          try {
            await onSubmit(values);
            reset();
          } catch {
            // Surfaced by the parent as a toast: the values stay so they need not be retyped.
            resetField('confirmPassword');
          }
        })(event);
      }}
      noValidate
      className="flex flex-col gap-4"
    >
      <p className="rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
        {t('admin.accounts.create.note')}
      </p>

      <Field
        label={t('admin.accounts.create.emailLabel')}
        {...whenDefined('error', errors.email?.message)}
      >
        {(bind) => <Input {...bind} {...register('email')} type="email" autoComplete="off" />}
      </Field>

      <Field
        label={t('admin.accounts.create.displayNameLabel')}
        {...whenDefined('error', errors.displayName?.message)}
      >
        {(bind) => <Input {...bind} {...register('displayName')} type="text" autoComplete="off" />}
      </Field>

      <Field
        label={t('admin.common.reasonLabel')}
        {...whenDefined('error', errors.reason?.message)}
      >
        {(bind) => <Input {...bind} {...register('reason')} type="text" autoComplete="off" />}
      </Field>

      <Field
        label={t('admin.common.confirmPasswordLabel')}
        hint={t('admin.common.confirmPasswordHint')}
        {...whenDefined('error', errors.confirmPassword?.message)}
      >
        {(bind) => (
          <Input
            {...bind}
            {...register('confirmPassword')}
            type="password"
            autoComplete="current-password"
          />
        )}
      </Field>

      <Button type="submit" loading={isSubmitting}>
        {t('admin.accounts.create.submit')}
      </Button>
    </form>
  );
}
